import React, { useContext, useState } from "react";
import Layout from "../../components/Layout/Layout";
import ProductType, {
    ClientProductTypes,
    isClientProductType
} from "../../../common/interfaces/enums/ProductType";
import cx from "classnames";
import styles from "./ProductSettingPage.module.scss";
import { getProductTypeAsString } from "../../../common/shared/enumHelpers";
import Icon from "../../../common/components/Icon/Icon";
import ProductFormContainer from "../../containers/ProductFormContainer/ProductFormContainer";
import LContainer from "../../components/LContainer/LContainer";
import { QuoteGroupContext } from "../../providers/RootContextProvider";
import BaseProductForm from "../../components/Products/BaseProductForm";
import SettingNavigation from "../../components/SettingNavigation/SettingNavigation";
import { useHistory, useLocation } from "react-router-dom";
import LifeCompareCoverAmountsModal from "../../components/Modals/LifeCompareCoverAmountsModal/LifeCompareCoverAmountsModal";
import { SmokerStatus } from "../../../common/interfaces/enums/SmokerStatus";
import { IClient } from "../../../common/interfaces/IClient";
import IBarGraphData from "../../interfaces/IBarGraphData";
import { addQuoteToGroupAPI, createQuoteAPI } from "../../api/endpoints/ApiQuotes";
import { retrieveGroupMapper } from "../../api/mapper/mapper";
import ProductWarnings from "../../components/ProductWarnings/ProductWarnings";
import { convertErrorsToTouched } from "../WizardPage";
import { ProductSelectionState } from "../../components/Quotes/Components/QuoteSettingsEditor/QuoteSettingsEditor";
import useAPI from "../../hooks/useAPI";

interface IProductSettingPageProps {
    productType: ClientProductTypes;
}

const ProductSettingPage: React.FC<IProductSettingPageProps> = ({ productType }) => {
    const { selectors, actions } = useContext(QuoteGroupContext);
    const [isOpen, setIsOpen] = useState(false);
    const history = useHistory();
    const returnTarget = useLocation();
    const search = new URLSearchParams(location.search);
    const { setProductSelected } = useAPI();

    const getProductState = (): ProductSelectionState => {
        const state: ProductSelectionState = {};

        selectors.getAllSettings().forEach(
            (s) =>
                (state[s.productType] = {
                    hasSettings: s.caseId > 0,
                    isSelected:
                        isClientProductType(s.productType) &&
                        selectors.isProductEnabled(s.productType),
                    hasChanged: false
                })
        );

        return state;
    };

    const [productState, setProductState] = useState<ProductSelectionState>(
        getProductState()
    );

    const updateProductState = async (): Promise<void> => {
        Object.keys(productState)
            .filter((s) => productState[s]?.hasChanged)
            .map(async (s: ClientProductTypes) =>
                setProductSelected(s, productState[s]?.isSelected)
            );
    };

    // Note: With multi-compare v5, we are always "editing"
    const isEditing = search.has("edit") || selectors.hasCreatedQuote();

    const getGraphData = (clients: IClient[]): IBarGraphData[] =>
        clients
            .filter((client) => !client.isChild)
            .map((client) => {
                return {
                    age: client.age,
                    isSmoker: client.smokerStatus === SmokerStatus.Smoker,
                    gender: client.gender
                };
            });

    return (
        <Layout
            hideMenu={!isEditing}
            progressBarCurrentStage={"build"}>
            <BaseProductForm
                clients={selectors.getClients()}
                compareProducts={productType}
                settings={selectors.getAllSettings()}
                onSubmit={async (settings) => {
                    const setting = settings.find((s) => s.productType === productType);

                    // If we can't create a setting, bail
                    if (!setting) return;

                    const mapper = retrieveGroupMapper(setting.productType);

                    const caseId = await createQuoteAPI(
                        mapper,
                        selectors.getClients(),
                        setting
                    );
                    await addQuoteToGroupAPI(selectors.getGroupId(), caseId);

                    actions.setQuoteConfig({
                        ...setting,
                        caseId
                    });

                    actions.setIsStreamlineFlow(false);
                    // Go to the results page
                    history.push(`/results`);
                }}>
                {({
                    handleSubmit,
                    isValid,
                    submitCount,
                    isSubmitting,
                    validateForm,
                    touched,
                    setTouched
                }) => (
                    <React.Fragment>
                        <div className={cx("layout-flex-column--centered")}>
                            <LContainer
                                className={cx(
                                    "panel-setting",
                                    "layout-flex-column--centered"
                                )}>
                                <Icon id="SettingsCog" />
                                <h2 className="text-primary text-align--center gap-4xl">
                                    Enter your {getProductTypeAsString(productType, true)}{" "}
                                    cover settings
                                </h2>
                                <ProductWarnings
                                    currentProduct={productType}
                                    className="gap-xl"
                                />
                                <ProductFormContainer
                                    productType={productType}
                                    className={cx(
                                        styles["form-content"],
                                        "layout-flex-column--centered"
                                    )}
                                />
                                {productType === ProductType.Life && (
                                    <React.Fragment>
                                        <span className="layout-flex-row--centered gap-pre-l">
                                            <Icon
                                                id="HowMuchDoOthers"
                                                className="space-m"
                                            />
                                            <a
                                                className="layout-flex-column text-font-size-s"
                                                onClick={() => setIsOpen(true)}>
                                                <span>How much do other</span>
                                                <span className="text-line-height-s">
                                                    people apply for?
                                                </span>
                                            </a>
                                        </span>
                                        <LifeCompareCoverAmountsModal
                                            isOpen={isOpen}
                                            onClose={() => setIsOpen(false)}
                                            clientSettings={getGraphData(
                                                selectors.getClients()
                                            )}
                                        />
                                    </React.Fragment>
                                )}
                            </LContainer>
                        </div>
                        <SettingNavigation
                            isLoading={isSubmitting}
                            onClickCallToAction={
                                productState.life.isSelected &&
                                productState.life.hasChanged &&
                                returnTarget.search === ""
                                    ? async (): Promise<void> => {
                                          const errors = await validateForm();

                                          if (Object.keys(errors).length > 0) {
                                              const errorsTouched = convertErrorsToTouched(
                                                  errors,
                                                  touched
                                              );

                                              setTouched(errorsTouched);
                                              return;
                                          }

                                          history.push({
                                              pathname: `/life/settings`,
                                              search: `/mortgage/settings`
                                          });
                                      }
                                    : !productState.life.isSelected &&
                                      productState.life.hasChanged &&
                                      returnTarget.search === ""
                                    ? updateProductState() && handleSubmit
                                    : handleSubmit
                            }
                            isDisabledCallToAction={submitCount > 0 && !isValid}
                            onClickBack={() => {
                                if (returnTarget.search !== "") {
                                    let pathName = returnTarget.search;
                                    pathName = pathName.substring(1);
                                    history.push(pathName);
                                } else {
                                    history.push(`/results`);
                                }
                            }}
                            gaAttributes={{
                                action: "setting"
                            }}
                            isEditing={true}
                        />
                    </React.Fragment>
                )}
            </BaseProductForm>
        </Layout>
    );
};

export default ProductSettingPage;
